import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { Box, Grid, Typography } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { startOfWeek, format, addDays, subDays } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { clientActions, utilizationActions } from '../../../../store';

import 'react-circular-progressbar/dist/styles.css';
import { FORTA_BLUE, FORTA_GRAY2, FORTA_GRAY3, FORTA_WHITE } from '../../../../utils/colors';
import { getUtcOffset } from '../../../../utils/common';
import { FHAutocomplete, FHProgressMeter, FHSkeletonLoader } from '../../../base';
import { formatSecondsToHHMM } from '../../../../helpers';
import SessionUtilizationBarChart from './SessionUtilizationBarChart';
import { getChartColor, getHelperText1, getHelperText2, isMonthSame } from '../utils';

import type { HTMLAttributes } from 'react';
import type { IClient, IOption, ITherapyUtilization } from '../../../../utils/types';
import type { RootState } from '../../../../store';

function UtilizationSummary() {
  const dispatch = useDispatch();
  const { t } = useTranslation('dashboard');
  const { getAccessTokenSilently } = useAuth0();
  const { list: clients, loading: clientsLoading } = useSelector(
    (state: RootState) => state.clients,
  );
  const { data: utilizations, loading: utilizationLoading } = useSelector(
    (state: RootState) => state.utilizations,
  );

  const [selectedClient, setSelectedClient] = useState<IOption | null>(null);
  const [previousWeekUtilization, setPreviousWeekUtilization] =
    useState<ITherapyUtilization | null>(null);
  const [currentWeekUtilization, setCurrentWeekUtilization] = useState<ITherapyUtilization | null>(
    null,
  );
  const [sortedUtilizations, setSortedUtilization] = useState<ITherapyUtilization[]>([]);
  const [helperText1, setHelperText1] = useState<string>('');
  const [helperText2, setHelperText2] = useState<string>('');

  useEffect(() => {
    dispatch(clientActions.getList({ getAccessTokenSilently }));
  }, [dispatch, getAccessTokenSilently]);

  useEffect(() => {
    if (clients.length > 0 && !selectedClient) {
      const { id, first_name, last_name } = clients[0];
      setSelectedClient({
        id,
        label: `${first_name} ${last_name}`,
      });
    }
  }, [clients]);

  useEffect(() => {
    if (clients.length === 0 || !selectedClient) return;

    const clientIds = selectedClient.id;
    const startDate = format(
      subDays(startOfWeek(new Date(), { weekStartsOn: 6 }), 7 * 5),
      'yyyy-MM-dd',
    );
    const endDate = format(addDays(startOfWeek(new Date(), { weekStartsOn: 6 }), 6), 'yyyy-MM-dd');
    const utcOffset = getUtcOffset(moment());

    dispatch(
      utilizationActions.getUtilization({
        getAccessTokenSilently,
        clientIds,
        startDate,
        endDate,
        utcOffset,
      }),
    );
  }, [clients, selectedClient]);

  useEffect(() => {
    if (!utilizations?.length) {
      setPreviousWeekUtilization(null);
      setCurrentWeekUtilization(null);
      setSortedUtilization([]);
      setHelperText1('');
      setHelperText2('');
      return;
    }

    const sorted = [...utilizations].sort(
      (a, b) => new Date(a.week_starts_on).getTime() - new Date(b.week_starts_on).getTime(),
    );

    const prevWeekUtilization = sorted.length > 2 ? sorted[sorted.length - 2] : null;
    const currWeekUtilization = sorted[sorted.length - 1];

    setPreviousWeekUtilization(prevWeekUtilization);
    setCurrentWeekUtilization(currWeekUtilization);
    setHelperText1(
      getHelperText1(t, currWeekUtilization?.utilization ?? 0),
    );
    setHelperText2(
      getHelperText2(t, currWeekUtilization?.utilization ?? 0),
    );
    setSortedUtilization(sorted);
  }, [t, utilizations]);

  const clientOptions = useMemo(
    () =>
      clients.map((client: IClient) => ({
        id: client.id,
        label: `${client.first_name} ${client.last_name}`,
      })),
    [clients],
  );

  const loading = clientsLoading || utilizationLoading;

  return (
    <>
      {!clientsLoading && clients.length === 0 && (
        <Typography variant="h6">No clients found</Typography>
      )}
      {
        <Box>
          <Box
            sx={{
              display: 'flex',
              flexGrow: 1,
              justifyContent: 'flex-start',
              width: '100%',
              alignItems: 'center',
              gap: 2,
              mt: 1,
              mb: { xs: 3, md: 0 },
            }}
          >
            <Typography fontSize={21} textAlign="left" color={FORTA_BLUE.primary}>
              {t('utilization.title')}
            </Typography>
            {clientsLoading ? (
              <FHSkeletonLoader width={300} height={40} />
            ) : clients.length === 1 ? (
              <Typography
                fontSize={21}
                textAlign="left"
                color={FORTA_BLUE.primary}
                fontWeight={700}
              >
                {clients[0].first_name + ' ' + clients[0].last_name}
              </Typography>
            ) : (
              <FHAutocomplete
                sx={{
                  '& input::placeholder': {
                    color: `${FORTA_GRAY3} !important`,
                  },
                  boxShadow: 'none',
                }}
                minWidth={300}
                inputHeight={40}
                disableClearable
                value={selectedClient}
                placeholder="Select client"
                isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                renderOption={(props: HTMLAttributes<HTMLLIElement>, option: IOption) => {
                  return (
                    <li {...props} key={option.id} data-testid={`clientLabel${option.id}`}>
                      {option.label}
                    </li>
                  );
                }}
                options={clientOptions}
                onChange={(_: any, newValue: any) => {
                  setSelectedClient(newValue);
                }}
              />
            )}
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={12} md={12} lg={6}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  minHeight: '575px',
                  backgroundColor: FORTA_WHITE,
                  mt: 4,
                  borderRadius: '20px',
                  p: 3,
                }}
              >
                <Box>
                  <Typography fontSize={21} textAlign={'left'} color={FORTA_BLUE.primary}>
                    {t('utilization.currentWeek.title')}
                  </Typography>
                  {loading ? (
                    <FHSkeletonLoader width={150} height={15} sx={{ mt: 1 }} />
                  ) : currentWeekUtilization ? (
                    <Typography fontSize={14} textAlign={'left'} color={FORTA_GRAY3}>
                      {`${moment(currentWeekUtilization?.week_starts_on).format('MMMM D')} ${
                        previousWeekUtilization
                          ? isMonthSame(
                              currentWeekUtilization?.week_ends_on,
                              previousWeekUtilization?.week_ends_on,
                            )
                            ? `to ${moment(currentWeekUtilization?.week_ends_on).format('D')}`
                            : `to ${moment(currentWeekUtilization?.week_ends_on).format('MMMM D')}`
                          : ''
                      }`}
                    </Typography>
                  ) : (
                    <Typography fontSize={14} textAlign={'left'} color={FORTA_GRAY3}>
                      Not available
                    </Typography>
                  )}
                  <Box sx={{ mt: 4, mb: 3 }}>
                    <Grid container mb={7}>
                      <Grid
                        item
                        sx={{ borderRight: `1px solid ${FORTA_GRAY2}` }}
                        xs={4}
                        textAlign="left"
                      >
                        <Typography fontSize={14} color={FORTA_GRAY3}>
                          {t('utilization.currentWeek.recommendedHours')}
                        </Typography>
                        <Typography fontWeight={700}>
                          {loading ? (
                            <FHSkeletonLoader width={100} height={15} sx={{ mt: 1 }} />
                          ) : currentWeekUtilization ? (
                            currentWeekUtilization?.hours_allowed < 1 ? (
                              `0 h ${(currentWeekUtilization?.hours_allowed * 3600) / 60} m`
                            ) : (
                              formatSecondsToHHMM(currentWeekUtilization?.hours_allowed * 3600)
                            )
                          ) : (
                            '--'
                          )}
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        sx={{ borderRight: `1px solid ${FORTA_GRAY2}`, pl: 2 }}
                        xs={4}
                        textAlign="left"
                      >
                        <Typography fontSize={14} color={FORTA_GRAY3}>
                          {t('utilization.currentWeek.lastWeek')}
                        </Typography>
                        <Typography fontWeight={700}>
                          {loading ? (
                            <FHSkeletonLoader width={100} height={15} sx={{ mt: 1 }} />
                          ) : previousWeekUtilization ? (
                            previousWeekUtilization?.hours_completed < 1 ? (
                              `0 h ${(previousWeekUtilization?.hours_completed * 3600) / 60} m`
                            ) : (
                              formatSecondsToHHMM(previousWeekUtilization?.hours_completed * 3600)
                            )
                          ) : (
                            '--'
                          )}
                        </Typography>
                      </Grid>
                      <Grid item xs={4} sx={{ pl: 2 }} textAlign="left">
                        <Typography fontSize={14} color={FORTA_GRAY3}>
                          {t('utilization.currentWeek.currentWeek')}
                        </Typography>
                        <Typography fontWeight={700}>
                          {loading ? (
                            <FHSkeletonLoader width={100} height={15} sx={{ mt: 1 }} />
                          ) : currentWeekUtilization ? (
                            currentWeekUtilization?.hours_completed < 1 ? (
                              `0 h ${(currentWeekUtilization?.hours_completed * 3600) / 60} m`
                            ) : (
                              formatSecondsToHHMM(currentWeekUtilization?.hours_completed * 3600)
                            )
                          ) : (
                            '--'
                          )}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '100%',
                      mb: -5,
                    }}
                  >
                    <Box sx={{ width: '350px', height: '260px' }}>
                      <FHProgressMeter
                        loading={loading}
                        chartColor={getChartColor(currentWeekUtilization)}
                        progressPercentage={Math.ceil(currentWeekUtilization?.utilization ?? 0)}
                      />
                    </Box>
                  </Box>
                </Box>
                <Box mt={-5} minHeight={'140px'}>
                  {loading ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        alignItems: 'center',
                      }}
                    >
                      <FHSkeletonLoader width={400} height={24} />
                      <FHSkeletonLoader width={300} height={24} sx={{ mb: 3 }} />
                      <FHSkeletonLoader width={500} height={24} />
                      <FHSkeletonLoader width={300} height={24} sx={{ mb: 3 }} />
                    </Box>
                  ) : (
                    <>
                      <Typography fontSize={24} p={2} lineHeight={1.3}>
                        {helperText1}
                      </Typography>
                      <Typography fontSize={20} color={FORTA_GRAY3} mt={1} mb={3}>
                        {helperText2}
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={6}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '575px',
                  backgroundColor: FORTA_WHITE,
                  borderRadius: '20px',
                  mt: 4,
                  p: 3,
                }}
              >
                <Box sx={{ flexGrow: 1, width: '100%' }}>
                  <Typography fontSize={21} textAlign={'left'} color={FORTA_BLUE.primary}>
                    {t('utilization.sixWeekUtilization.title')}
                  </Typography>
                  {loading ? (
                    <FHSkeletonLoader width={150} height={15} sx={{ mt: 1 }} />
                  ) : sortedUtilizations.length > 0 ? (
                    <Typography fontSize={14} textAlign={'left'} color={FORTA_GRAY3}>
                      {`${moment(sortedUtilizations?.[0]?.week_starts_on).format('MMMM D')} to ${
                        isMonthSame(
                          sortedUtilizations?.[0]?.week_starts_on,
                          sortedUtilizations?.[sortedUtilizations?.length - 1]?.week_ends_on,
                        )
                          ? moment(
                              sortedUtilizations?.[sortedUtilizations?.length - 1]?.week_ends_on,
                            ).format('D')
                          : moment(
                              sortedUtilizations?.[sortedUtilizations?.length - 1]?.week_ends_on,
                            ).format('MMMM D')
                      }`}
                    </Typography>
                  ) : (
                    <Typography fontSize={14} textAlign={'left'} color={FORTA_GRAY3}>
                      Not available
                    </Typography>
                  )}
                </Box>
                <Box
                  sx={{
                    width: '580px',
                    height: '450px',
                  }}
                >
                  <SessionUtilizationBarChart loading={loading} utilization={sortedUtilizations} />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      }
    </>
  );
}

export default UtilizationSummary;
